/**
 * Map a raw RPC/Postgres error to user-friendly copy. The verify/unverify/pool RPCs
 * RAISE EXCEPTION with developer-facing strings ('permission_denied: ...', 'already
 * verified by another rep ...'); never surface those verbatim to field reps. Unknown
 * errors fall back to a generic message rather than leaking the raw text.
 */
export const friendlyError = (e: unknown, fallback: string): string => {
  // supabase errors are plain objects with a message, not always Error instances
  const objMessage = (e as { message?: unknown } | null)?.message;
  const raw = (
    typeof objMessage === "string" ? objMessage : String(e ?? "")
  ).toLowerCase();
  if (raw.includes("msr_day_closed_verify")) {
    // MSR slice 4 (migration 503): a closed day's sales are final.
    return "This sign-up is dated on a day that's already closed, so its sales are final. An admin can add or remove it with a correction of that day (Event Detail).";
  }
  if (raw.includes("not assigned to this event")) {
    return "You're not assigned to this event, so you can't view or change its sign-ups. Ask a manager to add you.";
  }
  if (raw.includes("not in the signup pool")) {
    // Most often: the member matched overlapping shows and was just confirmed on the other one (mig 1390), so it
    // left this show's list after the page loaded.
    return "This member was already confirmed on another show — refresh the list.";
  }
  if (raw.includes("already verified by another rep")) {
    return "Another rep already verified this sign-up. A manager can reassign it.";
  }
  if (raw.includes("only a manager may credit another rep")) {
    return "Only a manager can credit a verification to another rep.";
  }
  if (raw.includes("credited rep must be assigned")) {
    return "That rep isn't an active assignee on this event.";
  }
  if (raw.includes("no sales position to credit")) {
    return "You don't have an active sales position to credit this to.";
  }
  if (raw.includes("not created by verification")) {
    return "This attribution came from a CTA override, not a verification, so it can't be removed here.";
  }
  if (raw.includes("only the verifier or a manager")) {
    return "Only the rep who verified this (or a manager) can remove it.";
  }
  if (raw.includes("no verified signup found")) {
    return "There's no verification to remove for this sign-up.";
  }
  if (raw.includes("permission_denied")) {
    return "You don't have permission to do that.";
  }
  return fallback;
};
